import { CopyButton } from "@components/shared/CopyButton";
import { GENERATE_BRANDED_JOB_POSTING, UPDATE_CAMPAIGN_JOB_POSTING } from "@lib/graphql/mutations";
import { CAMPAIGN_BRANDED_JOB_POSTING_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { AlertTriangle, ArrowLeft, Download, FileText, Loader2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router";
import remarkGfm from "remark-gfm";
import { useMutation, useQuery } from "urql";

interface BrandedJobPostingVersion {
  id: string;
  body: string;
  generationState: "draft" | "generating" | "error";
  generationError: string | null;
  model: string | null;
  versionNumber: number;
  effectiveFrom: string;
  changeSummary: string | null;
}

interface BrandedJobPosting {
  id: string;
  kind: string;
  totalVersions: number;
  currentVersion: BrandedJobPostingVersion | null;
}

interface CampaignData {
  id: string;
  title: string;
  brandedJobPosting: BrandedJobPosting | null;
}

interface OrgData {
  id: string;
  name: string;
  branding: Record<string, string> | null;
  logoUrl: string | null;
}

interface Branding {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  headingFont: string;
  bodyFont: string;
}

const DEFAULT_BRANDING: Branding = {
  primaryColor: "#1e293b",
  secondaryColor: "#475569",
  accentColor: "#2563eb",
  headingFont: "Inter, system-ui, sans-serif",
  bodyFont: "Inter, system-ui, sans-serif",
};

function resolveBranding(raw: Record<string, string> | null | undefined): Branding {
  const b = raw ?? {};
  return {
    primaryColor: b.primary_color?.trim() || DEFAULT_BRANDING.primaryColor,
    secondaryColor: b.secondary_color?.trim() || DEFAULT_BRANDING.secondaryColor,
    accentColor: b.accent_color?.trim() || DEFAULT_BRANDING.accentColor,
    headingFont: b.heading_font?.trim() || DEFAULT_BRANDING.headingFont,
    bodyFont: b.body_font?.trim() || DEFAULT_BRANDING.bodyFont,
  };
}

export function BrandedJobPostingPage() {
  const { id } = useParams();
  const [{ data, fetching }, reexecute] = useQuery<{
    campaign: CampaignData;
    currentOrganisation: OrgData;
  }>({
    query: CAMPAIGN_BRANDED_JOB_POSTING_QUERY,
    variables: { id },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(id!, refresh);

  const [, generateMutation] = useMutation(GENERATE_BRANDED_JOB_POSTING);
  const [{ fetching: saving }, updateMutation] = useMutation(UPDATE_CAMPAIGN_JOB_POSTING);

  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastVersionIdRef = useRef<string | null>(null);

  const campaign = data?.campaign;
  const org = data?.currentOrganisation;
  const posting = campaign?.brandedJobPosting ?? null;
  const currentVersion = posting?.currentVersion ?? null;
  const generationState = currentVersion?.generationState ?? null;

  const branding = useMemo(() => resolveBranding(org?.branding), [org?.branding]);

  useEffect(() => {
    if (!currentVersion) {
      setBody("");
      setDirty(false);
      lastVersionIdRef.current = null;
      return;
    }
    if (currentVersion.id === lastVersionIdRef.current) return;
    lastVersionIdRef.current = currentVersion.id;
    setBody(currentVersion.body ?? "");
    setDirty(false);
  }, [currentVersion]);

  const handleGenerate = async () => {
    setErrorMessage(null);
    const result = await generateMutation({ input: { campaignId: id } });
    const errors = result.data?.generateBrandedJobPosting?.errors ?? [];
    if (errors.length) setErrorMessage(errors.join("; "));
    refresh();
  };

  const handleSave = async () => {
    if (!posting) return;
    setErrorMessage(null);
    const result = await updateMutation({ input: { id: posting.id, body } });
    const errors = result.data?.updateCampaignJobPosting?.errors ?? [];
    if (errors.length) {
      setErrorMessage(errors.join("; "));
      return;
    }
    setDirty(false);
  };

  const handleDownload = () => {
    if (!campaign) return;
    const html = buildStandaloneHtml({
      title: campaign.title,
      body,
      organisation: org,
      branding,
    });
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${slugify(campaign.title)}-job-posting.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (fetching && !campaign) {
    return <div className="p-6 text-sm text-text-muted">Loading branded job posting…</div>;
  }

  if (!campaign) {
    return <div className="p-6 text-sm text-text-muted">Campaign not found.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link
          to={`/campaigns/${campaign.id}/overview`}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
        >
          <ArrowLeft size={14} /> Back to campaign
        </Link>
        {currentVersion && (
          <span className="text-xs text-text-faint" data-testid="version-count">
            Version {currentVersion.versionNumber} · {posting?.totalVersions ?? 0} total
          </span>
        )}
      </div>

      <div className="mb-4 flex items-start gap-3">
        <FileText size={22} className="mt-1 text-accent" />
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Branded job posting</h1>
          <p className="text-sm text-text-muted">
            Drafted for your organisation's careers site or job boards. Preview shows your branding;
            edit the markdown on the left.
          </p>
        </div>
      </div>

      {errorMessage && (
        <div
          className="mb-4 flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="error-message"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {!currentVersion && <EmptyState onGenerate={handleGenerate} />}

      {generationState === "generating" && <GeneratingState />}

      {(generationState === "draft" || generationState === "error") && currentVersion && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Markdown
              </span>
              <div className="flex items-center gap-1">
                <CopyButton
                  ariaLabel="Copy as markdown"
                  withLabel
                  getContent={() => ({ text: body, html: body })}
                />
              </div>
            </div>
            <textarea
              aria-label="Branded job posting body"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setDirty(e.target.value !== (currentVersion.body ?? ""));
              }}
              rows={28}
              className="w-full resize-y rounded border border-border bg-background p-3 font-mono text-xs text-text-primary focus:border-accent focus:outline-none"
            />
            {generationState === "error" && currentVersion.generationError && (
              <div className="mt-2 flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{currentVersion.generationError}</span>
              </div>
            )}
            <div className="mt-3 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleGenerate}
                className="rounded px-3 py-1.5 text-xs text-text-muted hover:bg-card-hover hover:text-text-secondary"
              >
                Regenerate
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="flex items-center gap-1 rounded border border-border px-3 py-1.5 text-xs text-text-secondary hover:bg-card-hover"
                data-testid="download-button"
              >
                <Download size={12} /> Download HTML
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!dirty || saving}
                className="flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                Save
              </button>
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Branded preview
              </span>
            </div>
            <BrandedPreview body={body} branding={branding} org={org} />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onGenerate }: { onGenerate: () => void }) {
  return (
    <div className="rounded-lg bg-card p-8 text-center shadow-sm ring-1 ring-ring">
      <FileText size={32} className="mx-auto text-text-faint" />
      <h3 className="mt-3 text-sm font-medium text-text-primary">No branded job posting yet</h3>
      <p className="mt-1 text-xs text-text-muted">
        Draft a branded posting from the campaign brief and role blueprint.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        className="mt-4 inline-flex items-center gap-1 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
      >
        <Sparkles size={12} /> Generate
      </button>
    </div>
  );
}

function GeneratingState() {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-card p-4 text-sm text-text-muted shadow-sm ring-1 ring-ring">
      <Loader2 size={16} className="animate-spin" /> Drafting branded job posting…
    </div>
  );
}

interface BrandedPreviewProps {
  body: string;
  branding: Branding;
  org: OrgData | undefined;
}

function BrandedPreview({ body, branding, org }: BrandedPreviewProps) {
  const previewStyle: React.CSSProperties & Record<string, string> = {
    fontFamily: branding.bodyFont,
    color: branding.secondaryColor,
    "--preview-primary": branding.primaryColor,
    "--preview-accent": branding.accentColor,
    "--preview-heading-font": branding.headingFont,
  };

  return (
    <div
      data-testid="branded-preview"
      data-primary-color={branding.primaryColor}
      style={previewStyle}
      className="rounded border border-border bg-white p-6 shadow-sm dark:bg-slate-900"
    >
      {org?.logoUrl && (
        <img
          src={org.logoUrl}
          alt={`${org.name} logo`}
          className="mb-4 h-10 w-auto object-contain"
          data-testid="branded-logo"
        />
      )}
      <div className="prose prose-sm max-w-none [&_h1]:mb-3 [&_h1]:mt-0 [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:font-semibold [&_li]:my-0.5 [&_p]:leading-relaxed">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1
                style={{
                  color: branding.primaryColor,
                  fontFamily: branding.headingFont,
                  fontSize: "1.5rem",
                }}
              >
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2
                style={{
                  color: branding.accentColor,
                  fontFamily: branding.headingFont,
                  fontSize: "1.125rem",
                }}
              >
                {children}
              </h2>
            ),
          }}
        >
          {body || "_Empty — edit the markdown on the left to see a preview._"}
        </ReactMarkdown>
      </div>
    </div>
  );
}

interface StandaloneHtmlArgs {
  title: string;
  body: string;
  organisation: OrgData | undefined;
  branding: Branding;
}

// Build a self-contained HTML file the recruiter can open, print, or publish.
// All styling is inlined — no external fonts or stylesheets — so the file
// works offline and reproduces the preview's look exactly.
function buildStandaloneHtml({ title, body, organisation, branding }: StandaloneHtmlArgs): string {
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const html = renderMarkdownForDownload(body, branding);
  const orgName = organisation?.name ? esc(organisation.name) : "";
  const logoTag = organisation?.logoUrl
    ? `<img src="${esc(organisation.logoUrl)}" alt="${orgName} logo" class="logo" />`
    : "";
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>
  body { font-family: ${branding.bodyFont}; color: ${branding.secondaryColor}; margin: 0; padding: 2.5rem; max-width: 720px; line-height: 1.6; }
  .logo { height: 56px; width: auto; margin-bottom: 1.5rem; display: block; }
  h1 { color: ${branding.primaryColor}; font-family: ${branding.headingFont}; font-size: 1.8rem; margin-top: 0; margin-bottom: 0.75rem; }
  h2 { color: ${branding.accentColor}; font-family: ${branding.headingFont}; font-size: 1.2rem; margin-top: 1.75rem; margin-bottom: 0.5rem; }
  ul { padding-left: 1.25rem; }
  li { margin: 0.25rem 0; }
  p { margin: 0.5rem 0; }
</style>
</head>
<body>
${logoTag}
${html}
</body>
</html>`;
}

// Minimal markdown→HTML for the downloaded file. Handles #, ##, list items,
// blank-line paragraph breaks. Intentionally small — no external deps inlined.
function renderMarkdownForDownload(body: string, _branding: Branding): string {
  const lines = body.split(/\r?\n/);
  const out: string[] = [];
  let inList = false;
  let paragraphBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) return;
    out.push(`<p>${escapeHtml(paragraphBuffer.join(" "))}</p>`);
    paragraphBuffer = [];
  };
  const closeList = () => {
    if (inList) {
      out.push("</ul>");
      inList = false;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line === "") {
      flushParagraph();
      closeList();
      continue;
    }
    const h1 = /^#\s+(.+)$/.exec(line);
    const h2 = /^##\s+(.+)$/.exec(line);
    const li = /^[-*]\s+(.+)$/.exec(line);
    if (h1) {
      flushParagraph();
      closeList();
      out.push(`<h1>${escapeHtml(h1[1])}</h1>`);
    } else if (h2) {
      flushParagraph();
      closeList();
      out.push(`<h2>${escapeHtml(h2[1])}</h2>`);
    } else if (li) {
      flushParagraph();
      if (!inList) {
        out.push("<ul>");
        inList = true;
      }
      out.push(`<li>${escapeHtml(li[1])}</li>`);
    } else {
      closeList();
      paragraphBuffer.push(line);
    }
  }
  flushParagraph();
  closeList();
  return out.join("\n");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 60) || "posting"
  );
}
